/**
 * Standards API
 * Manages standards catalog, project preferences, and corpus CRUD
 */

import { apiFetch, API_BASE_URL, getAuthHeader } from './config';
import type { StandardsCatalog, ProjectStandards, StandardsPreferences } from '../types';

export interface CorpusStandard {
  pattern_id: string;
  element: string;
  title: string;
  rule: string;
  category: string;
  domain: string;
  source: string;
  content_type: string;
  maturity: string;
  priority: number;
  description: string | null;
  rationale: string | null;
  consequences: string | null;
  exceptions: string | null;
  anti_patterns: string[] | null;
  examples: string[] | null;
  keywords: string[] | null;
  applicable_files: string[] | null;
  file_name: string;
  company_id: string | null;
  active: boolean;
  created_at: string;
  last_updated: string;
}

export interface CorpusExport {
  _meta: {
    exported_at: string;
    company_id: string;
    tables: string[];
    total_rows: number;
    exported_by: string;
  };
  standards: CorpusStandard[];
  documents?: unknown[];
  corrections?: unknown[];
  overrides?: unknown[];
  [key: string]: unknown;
}

export interface StandardCreateInput {
  element: string;
  title: string;
  rule: string;
  category: string;
  domain?: string;
  priority?: number;
  content_type?: string;
  maturity?: string;
  description?: string;
  rationale?: string;
  consequences?: string;
  exceptions?: string;
  anti_patterns?: string[];
  examples?: string[];
  keywords?: string[];
  applicable_files?: string[];
  file_name?: string;
}

export type StandardUpdateInput = Partial<StandardCreateInput> & { active?: boolean };

export interface StandardsListResponse {
  Records: CorpusStandard[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export const standardsApi = {
  listStandards: async (params?: {
    q?: string;
    category?: string;
    maturity?: string;
    content_type?: string;
    domain?: string;
    include_archived?: boolean;
    page?: number;
    limit?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.q) searchParams.set('q', params.q);
    if (params?.category) searchParams.set('category', params.category);
    if (params?.maturity) searchParams.set('maturity', params.maturity);
    if (params?.content_type) searchParams.set('content_type', params.content_type);
    if (params?.domain) searchParams.set('domain', params.domain);
    if (params?.include_archived) searchParams.set('include_archived', 'true');
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const query = searchParams.toString();
    return apiFetch<StandardsListResponse>(`/api/standards/corpus${query ? `?${query}` : ''}`);
  },

  getCatalog: async (techStack?: string) => {
    const params = new URLSearchParams();
    if (techStack) params.set('tech_stack', techStack);
    const query = params.toString();
    return apiFetch<StandardsCatalog>(`/api/standards/catalog${query ? `?${query}` : ''}`);
  },

  getProjectStandards: async (projectId: string) => {
    return apiFetch<ProjectStandards>(`/api/projects/${encodeURIComponent(projectId)}/standards`);
  },

  updateProjectStandards: async (
    projectId: string,
    preferences: Partial<StandardsPreferences>
  ) => {
    return apiFetch<ProjectStandards>(`/api/projects/${encodeURIComponent(projectId)}/standards`, {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  },

  exportCorpus: async (tables?: string, activeOnly = true) => {
    const params = new URLSearchParams();
    if (tables) params.set('tables', tables);
    if (!activeOnly) params.set('active_only', 'false');
    const query = params.toString();
    return apiFetch<CorpusExport>(`/api/standards/corpus/export${query ? `?${query}` : ''}`);
  },

  downloadCorpus: async (tables?: string, activeOnly = true) => {
    const params = new URLSearchParams();
    if (tables) params.set('tables', tables);
    if (!activeOnly) params.set('active_only', 'false');
    const query = params.toString();
    const response = await fetch(`${API_BASE_URL}/api/standards/corpus/export${query ? `?${query}` : ''}`, {
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    });
    const data = await response.json();
    const blob = new Blob([JSON.stringify(data.data || data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `corpus-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },

  createStandard: async (input: StandardCreateInput) => {
    return apiFetch<{ Records: CorpusStandard[] }>('/api/standards/corpus', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  updateStandard: async (patternId: string, input: StandardUpdateInput) => {
    return apiFetch<{ Records: CorpusStandard[] }>(`/api/standards/corpus/${encodeURIComponent(patternId)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  retireStandard: async (patternId: string, reason: string) => {
    const params = `?reason=${encodeURIComponent(reason)}`;
    return apiFetch<{ Records: unknown[] }>(`/api/standards/corpus/${encodeURIComponent(patternId)}${params}`, {
      method: 'DELETE',
    });
  },
};
